import '../styles/adminResponsive.css';
import { safeLocalStorage } from '../lib/safeStorage';
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Plus, Trash2, Mail, Clock, CheckCircle2, XCircle, Link2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
};

const StatusBadge = ({ invite }) => {
  if (invite.used) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-medium">
        <CheckCircle2 size={12} /> Kullanıldı
      </span>
    );
  }
  if (invite.revoked) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-200 text-zinc-700 text-xs font-medium">
        <XCircle size={12} /> İptal
      </span>
    );
  }
  const expired = invite.expires_at && new Date(invite.expires_at) < new Date();
  if (expired) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">
        <Clock size={12} /> Süresi doldu
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 text-xs font-medium">
      <Link2 size={12} /> Aktif
    </span>
  );
};

const AdminVenueInvites = () => {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  // A3: düz token sunucudan bir daha dönmez; link yalnız oluşturma yanıtında bir kez gelir ve yalnız burada tutulur.
  const [createdLink, setCreatedLink] = useState(null);
  const [form, setForm] = useState({
    email_hint: '',
    venue_name_hint: '',
    note: '',
    expires_in_days: 14,
  });

  const authHeader = useCallback(() => ({
    headers: { Authorization: `Bearer ${safeLocalStorage.getItem('mm_access_token')}` }
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/venue-invites`, authHeader());
      setInvites(res.data || []);
    } catch (e) {
      toast.error('Davetler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        email_hint: form.email_hint.trim() || null,
        venue_name_hint: form.venue_name_hint.trim() || null,
        note: form.note.trim() || null,
        expires_in_days: Number(form.expires_in_days) || 14,
      };
      const { data } = await axios.post(`${API}/api/admin/venue-invites`, payload, authHeader());
      const link = data?.invite_link || (data?.invite_path ? `${window.location.origin}${data.invite_path}` : null);
      setCreatedLink(link ? { id: data.id, link } : null);
      toast.success('Davet oluşturuldu');
      setForm({ email_hint: '', venue_name_hint: '', note: '', expires_in_days: 14 });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Davet oluşturulamadı');
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Davet bağlantısı kopyalandı');
    } catch {
      toast.error('Kopyalanamadı');
    }
  };

  const handleRevoke = async (inviteId) => {
    if (!window.confirm('Bu daveti iptal etmek istiyor musun?')) return;
    try {
      await axios.delete(`${API}/api/admin/venue-invites/${encodeURIComponent(inviteId)}`, authHeader());
      if (createdLink?.id === inviteId) setCreatedLink(null);
      toast.success('Davet iptal edildi');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'İptal edilemedi');
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10 admin-responsive-page" data-testid="admin-venue-invites-page">
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-semibold tracking-tight text-zinc-900">
          Mekân Davet Bağlantıları
        </h1>
        <p className="mt-2 text-sm text-zinc-500 max-w-2xl">
          Mekân sahiplerine tek tıkla kayıt olabilecekleri kişisel bir davet linki oluştur.
          Bu link ile kayıt olanlar hesap açar ve Meko ile mekân DNA’sını oluşturur; profil ekibimizin onayından sonra keşifte görünür.
          Link yalnız oluşturulduğunda bir kez gösterilir; kaybolursa iptal edip yenisini oluştur.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Create form */}
        <div className="lg:col-span-1">
          <form
            onSubmit={handleCreate}
            className="bg-white border border-zinc-200 rounded-2xl p-5 shadow-sm space-y-4"
            data-testid="invite-create-form"
          >
            <div className="flex items-center gap-2 text-zinc-900">
              <Plus size={18} />
              <h2 className="text-base font-semibold">Yeni Davet</h2>
            </div>
            <div>
              <label className="block text-xs text-zinc-600 mb-1">E-posta (opsiyonel)</label>
              <input
                type="email"
                value={form.email_hint}
                onChange={(e) => setForm({ ...form, email_hint: e.target.value })}
                placeholder="mekan@ornek.com"
                className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/20"
                data-testid="invite-email-input"
              />
              <p className="text-[11px] text-zinc-400 mt-1">Doldurursan, kayıt olurken e-posta bununla eşleşmeli.</p>
            </div>
            <div>
              <label className="block text-xs text-zinc-600 mb-1">Mekân adı (opsiyonel)</label>
              <input
                type="text"
                value={form.venue_name_hint}
                onChange={(e) => setForm({ ...form, venue_name_hint: e.target.value })}
                placeholder="Loft 34"
                className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/20"
                data-testid="invite-venue-name-input"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-600 mb-1">Not (yalnız admin görür)</label>
              <textarea
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                rows={2}
                placeholder="Ör: Cihangir'deki atölye…"
                className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/20"
                data-testid="invite-note-input"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-600 mb-1">Geçerlilik (gün)</label>
              <input
                type="number"
                min={1}
                max={90}
                value={form.expires_in_days}
                onChange={(e) => setForm({ ...form, expires_in_days: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/20"
                data-testid="invite-expires-input"
              />
            </div>
            <button
              type="submit"
              disabled={creating}
              className="w-full py-2.5 rounded-lg bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 transition-colors disabled:opacity-60"
              data-testid="invite-create-submit"
            >
              {creating ? 'Oluşturuluyor…' : 'Davet Oluştur'}
            </button>
            {createdLink && (
              <div className="bg-zinc-50 border border-zinc-100 rounded-lg px-3 py-2 space-y-2" data-testid="invite-created-link">
                <p className="text-xs text-zinc-600">Bu link yalnız şimdi gösteriliyor. Kopyalayıp paylaş:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs text-zinc-700 truncate font-mono">{createdLink.link}</code>
                  <button
                    type="button"
                    onClick={() => handleCopy(createdLink.link)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-[#1E3A8A] text-white hover:bg-[#e57200]"
                    data-testid={`invite-copy-${createdLink.id}`}
                  >
                    <Copy size={13} /> Kopyala
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Invites list */}
        <div className="lg:col-span-2">
          {loading ? (
            <div className="text-sm text-zinc-500">Yükleniyor…</div>
          ) : invites.length === 0 ? (
            <div className="bg-zinc-50 border border-dashed border-zinc-200 rounded-2xl p-10 text-center">
              <Mail className="mx-auto mb-3 text-zinc-400" size={28} />
              <p className="text-sm text-zinc-600">Henüz davet oluşturmadın.</p>
              <p className="text-xs text-zinc-400 mt-1">Soldaki formla ilk davetini oluştur.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="invite-list">
              {invites.map((inv) => {
                const active = !inv.used && !inv.revoked &&
                  (!inv.expires_at || new Date(inv.expires_at) >= new Date());
                return (
                  <div
                    key={inv.id}
                    className="bg-white border border-zinc-200 rounded-xl p-4 flex flex-col gap-3 admin-responsive-card"
                    data-testid={`invite-row-${inv.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <StatusBadge invite={inv} />
                          {inv.venue_name_hint && (
                            <span className="text-sm font-medium text-zinc-900 truncate">
                              {inv.venue_name_hint}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-xs text-zinc-500 space-y-0.5">
                          {inv.email_hint && <div>E-posta: {inv.email_hint}</div>}
                          {inv.note && <div>Not: {inv.note}</div>}
                          <div>Oluşturma: {fmtDate(inv.created_at)}</div>
                          <div>Geçerlilik: {fmtDate(inv.expires_at)}</div>
                          {inv.used_at && <div>Kullanım: {fmtDate(inv.used_at)}</div>}
                        </div>
                      </div>
                      {active && (
                        <button
                          onClick={() => handleRevoke(inv.id)}
                          title="Daveti iptal et"
                          className="p-2 rounded-lg text-zinc-400 hover:text-red-600 hover:bg-red-50"
                          data-testid={`invite-revoke-${inv.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                    {active && (
                      <p className="text-xs text-zinc-500" data-testid={`invite-link-hidden-${inv.id}`}>
                        Link güvenlik için yalnız oluşturulduğunda gösterilir. Kaybolduysa bu daveti iptal edip yenisini oluştur.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminVenueInvites;
